import React, { memo, useEffect, useRef } from "react";
import { Box, Tooltip, Typography, useTheme } from "@mui/material";
import { NightsStay as NightsStayIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import Logo from "../shared/Logo";
import MiniChart from "./Charts/MiniChart";
import { getSymbolImageUrl } from "../../lib/symbolImage";
import { hapticSelection } from "../../lib/haptics";
import { markNavStart } from "../../lib/navPerf";
import { useCompanyProfile } from "./Charts/hooks/useCompanyProfile";
import { useCompanyAftermarketTrade } from "./Charts/hooks/useCompanyAftermarketTrade";
import { useExchangeMarketHours } from "./Charts/hooks/useExchangeMarketHours";
import {
  formatStockPrice,
  getCurrencyFromExchange,
  getDisplayCurrencySymbol,
} from "../../utils/stockPriceFormatter";
import { formatNumber } from "./Charts/utils/formatNumber";
import { focusRingSx, panelBorder, panelSurface } from "./overviewPanel";
import { useMembership, FREE_TIER_TICKER } from "../../contexts/MembershipContext";

// The dashboard's compact company card: logo, ticker over a truncating name,
// an optional micro trend chart, the live price and day move, and the
// after-hours pair when the market is closed. One card serves watchlists,
// Trending, Top Gainers and the theme lists, so every list on the dashboard
// reads as the same system. Price flashes are verbatim CompactStockCard
// behaviour — DOM mutation, no re-renders.

export interface PulseStock {
  symbol: string;
  name: string;
  price: number;
  changePct: number;
  /** Absolute $ day change — only lists offering the metric switch pass it. */
  change?: number;
  marketCap?: number;
  /** When the list's payload already carries it, the exchange chip renders
   *  without waiting on the per-card profile fetch. */
  exchange?: string;
}

/** Which value rides under the price — the watchlist "Show" preference. */
export type PulseMetric = "changePct" | "change" | "marketCap";

interface CompanyPulseCardProps {
  stock: PulseStock;
  /** Micro sparkline — for watchlist/mover lists; dense theme lists omit it. */
  showChart?: boolean;
  /**
   * "large" gives the user's own watchlists a roomier card than the dense
   * market lists; "compact" is the overview watchlist's card — identity on
   * the left, the day's shape in the middle, prices anchoring the right.
   */
  size?: "regular" | "large" | "compact";
  /** Compact only: the metric under the price. Defaults to the day %. */
  show?: PulseMetric;
}

// Compact cards fix the identity column so every card's chart starts on
// the same x and claims ALL the remaining width — the column reads as one
// aligned instrument panel. Sized for the typical name ("Microsoft
// Corporation", "BlackSky Technology Inc.") rather than the outlier — a
// "Space Exploration Technologies Corp." ellipsizes so every other card's
// chart isn't paying for its length.
const COMPACT_TEXT_COL_W = 160;

const CompanyPulseCard: React.FC<CompanyPulseCardProps> = ({
  stock,
  showChart = false,
  size = "regular",
  show = "changePct",
}) => {
  const isLarge = size === "large";
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const navigate = useNavigate();
  // Free users preview one ticker (the CompactStockCard rule): every other
  // card is greyed, wears the Pro badge, and leads to the trial.
  const { isMembershipActive } = useMembership();
  const isAccessible = isMembershipActive || stock.symbol === FREE_TIER_TICKER;

  const { data: profileData } = useCompanyProfile(stock.symbol);
  const profile = Array.isArray(profileData) ? profileData[0] : profileData;
  const exchange = stock.exchange ?? profile?.exchange;
  const currency = profile?.currency ?? getCurrencyFromExchange(exchange);

  // Same hooks (and query keys) as CompactStockCard, so the react-query cache
  // is shared across the app. Aftermarket data is only shown when the market
  // is closed, so it isn't fetched during hours.
  const { data: marketHours } = useExchangeMarketHours(exchange);
  const { data: aftermarketData } = useCompanyAftermarketTrade(
    stock.symbol,
    marketHours?.isMarketOpen === false
  );
  const aftermarketTrade = Array.isArray(aftermarketData) ? aftermarketData[0] : aftermarketData;
  const showAfterHours = !!aftermarketTrade && marketHours?.isMarketOpen === false && stock.price > 0;
  const afterHoursPrice = aftermarketTrade?.price;
  const afterHoursChangePercentage =
    showAfterHours && afterHoursPrice !== undefined
      ? ((afterHoursPrice - stock.price) / stock.price) * 100
      : 0;
  const isAfterHoursPositive = afterHoursChangePercentage >= 0;

  // Detect price changes and flash the price text green/red briefly (DOM
  // manipulation to avoid re-renders) — verbatim CompactStockCard behaviour.
  const prevPriceRef = useRef<number>(stock.price);
  const priceRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const prev = prevPriceRef.current;
    if (stock.price > 0 && stock.price !== prev) {
      prevPriceRef.current = stock.price;
      const el = priceRef.current;
      if (!el) return;
      const flashColor = stock.price > prev ? "#22c55e" : "#ef4444";
      el.style.transition = "none";
      el.style.color = flashColor;
      const timer = setTimeout(() => {
        if (!priceRef.current) return;
        priceRef.current.style.transition = "color 0.4s ease";
        priceRef.current.style.color = "";
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [stock.price]);

  // Same flash behaviour for the after-hours price (skip the first observed
  // value so the initial fetch doesn't trigger a flash).
  const prevAhPriceRef = useRef<number | undefined>(undefined);
  const ahPriceRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (afterHoursPrice === undefined || afterHoursPrice <= 0) return;
    const prev = prevAhPriceRef.current;
    if (prev === undefined) {
      prevAhPriceRef.current = afterHoursPrice;
      return;
    }
    if (afterHoursPrice === prev) return;
    prevAhPriceRef.current = afterHoursPrice;
    const el = ahPriceRef.current;
    if (!el) return;
    const flashColor = afterHoursPrice > prev ? "#22c55e" : "#ef4444";
    el.style.transition = "none";
    el.style.color = flashColor;
    const timer = setTimeout(() => {
      if (!ahPriceRef.current) return;
      ahPriceRef.current.style.transition = "color 0.4s ease";
      ahPriceRef.current.style.color = "";
    }, 600);
    return () => clearTimeout(timer);
  }, [afterHoursPrice]);

  // A gated card is a DOOR, not a dead end. On a phone the tooltip below is
  // effectively invisible — it wants a hover, and a long-press is not a
  // gesture anyone thinks to try on a price card — so a tap that did nothing
  // read as a broken card. It now goes where the badge promises.
  const handleClick = () => {
    hapticSelection();
    if (!isAccessible) {
      navigate("/start-free-trial");
      return;
    }
    markNavStart("stock_card");
    navigate(`/charts?ticker=${encodeURIComponent(stock.symbol)}`);
  };

  // The card is a Box, not a <button>, so the keyboard contract is spelled
  // out: Enter and Space open it, like any button.
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter" && event.key !== " ") return;
    event.preventDefault();
    handleClick();
  };

  // Gated cards wrap in the same tooltip CompactStockCard uses — hover on
  // pointer, long-press on touch (MUI's default), focus via the keyboard.
  // It is the pointer user's explanation; the badge is everyone else's.
  const gated = (card: React.ReactElement) => {
    if (isAccessible) return card;
    return (
      <Tooltip title="Available with Insight Pro" arrow>
        {card}
      </Tooltip>
    );
  };

  // Says why the card is greyed WITHOUT waiting for a hover — on touch it is
  // the only explanation there is. The house free-tier badge, as worn by the
  // locked sector cards.
  const proBadge = (
    <Typography
      component="span"
      sx={{
        fontSize: "0.5625rem",
        fontWeight: 600,
        color: "#22c55e",
        border: "1px solid #22c55e",
        borderRadius: 0.5,
        px: 0.5,
        py: 0.125,
        lineHeight: 1.4,
        flexShrink: 0,
      }}
    >
      Pro
    </Typography>
  );

  // The card announces what it is now that it leads somewhere; "disabled"
  // would be a lie about a control that responds.
  const gatedLabel = isAccessible
    ? undefined
    : `${stock.symbol}, available with Insight Pro`;

  const isPositive = stock.changePct > 0;
  const isNegative = stock.changePct < 0;
  const changeColor = isPositive ? "#22c55e" : isNegative ? "#ef4444" : theme.palette.text.secondary;

  // A missing day % is "—" in quiet secondary — never a fabricated 0.00%.
  const dayPctText =
    stock.changePct == null ? "—" : `${isPositive ? "+" : ""}${stock.changePct.toFixed(2)}%`;

  // The compact card's line under the price: the chosen metric. % and $
  // change color by direction; market cap is not directional, so it stays
  // quiet secondary text. Missing data is "—", never a fabricated zero.
  let metricText: string;
  let metricColor: string = changeColor;
  if (show === "marketCap") {
    const cap = stock.marketCap ?? 0;
    metricText = cap > 0 ? `${getDisplayCurrencySymbol(currency)}${formatNumber(cap)}` : "—";
    metricColor = theme.palette.text.secondary;
  } else if (show === "change") {
    const change = stock.change;
    if (change == null || !Number.isFinite(change)) {
      metricText = "—";
      metricColor = theme.palette.text.secondary;
    } else {
      const magnitude = formatStockPrice({ price: Math.abs(change), currency });
      if (change > 0) {
        metricText = `+${magnitude}`;
        metricColor = "#22c55e";
      } else if (change < 0) {
        metricText = `-${magnitude}`;
        metricColor = "#ef4444";
      } else {
        metricText = magnitude;
        metricColor = theme.palette.text.secondary;
      }
    }
  } else {
    metricText = dayPctText;
  }

  // The after-hours pair mirrors the metric ONLY when the metric is a
  // session delta: a user reading the day in dollars shouldn't read the
  // evening in percent. Market cap has no after-hours analogue (it isn't a
  // delta), so the AH slot falls back to % — still saying what's moving.
  let ahMetricText = `${isAfterHoursPositive ? "+" : ""}${afterHoursChangePercentage.toFixed(2)}%`;
  if (show === "change" && showAfterHours && afterHoursPrice !== undefined) {
    const ahChange = afterHoursPrice - stock.price;
    const ahMagnitude = formatStockPrice({ price: Math.abs(ahChange), currency });
    ahMetricText = ahChange > 0 ? `+${ahMagnitude}` : ahChange < 0 ? `-${ahMagnitude}` : ahMagnitude;
  }

  if (size === "compact") {
    return gated(
      <Box
        role="button"
        tabIndex={0}
        aria-label={gatedLabel}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        sx={{
          ...focusRingSx,
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          px: 1.5,
          py: 1.25,
          borderRadius: 1.5,
          backgroundColor: panelSurface(isDark),
          border: `1px solid ${panelBorder(isDark)}`,
          cursor: "pointer",
          opacity: isAccessible ? 1 : 0.6,
          WebkitTapHighlightColor: "transparent",
          transition: "background-color 0.2s ease, border-color 0.2s ease, transform 0.15s ease",
          // The green "yours, tap in" gesture the news cards share: the
          // wash warms, the border and ticker light up, the press dips the
          // card.
          ...(isAccessible && {
            "&:hover": {
              backgroundColor: isDark ? "rgba(34, 197, 94, 0.05)" : "rgba(34, 197, 94, 0.04)",
              borderColor: "rgba(34, 197, 94, 0.45)",
              "& .pulse-ticker": { color: "#22c55e" },
            },
            "&:active": { transform: "scale(0.98)" },
          }),
          // A locked card presses too — it just leads somewhere else.
          ...(!isAccessible && {
            "&:hover": { borderColor: "rgba(34, 197, 94, 0.45)" },
            "&:active": { transform: "scale(0.98)" },
          }),
        }}
      >
        {/* Logo rides the top line with the ticker rather than centering on
            the card */}
        <Box sx={{ alignSelf: "flex-start", mt: 0.25, flexShrink: 0 }}>
          <Logo src={getSymbolImageUrl(stock.symbol)} alt={stock.symbol} ticker={stock.symbol} size={32} />
        </Box>
        {/* The identity column: ticker, name, and the price pair directly
            beneath the name it belongs to. Fixed at EVERY width (the prod
            CompactStockCard contract): the text never grows into the card's
            slack, so every card's chart starts on the same x and the trend
            gets all the room left over. The name ellipsizes into this width
            rather than widening it. overflow guards the one case text can't
            shrink into — a four-figure after-hours pair clips rather than
            running over the chart. */}
        <Box
          sx={{
            width: COMPACT_TEXT_COL_W,
            flex: "0 0 auto",
            minWidth: 0,
            overflow: "hidden",
          }}
        >
          {/* Line 1: ticker + exchange chip */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
            <Typography
              className="pulse-ticker"
              sx={{
                fontSize: "0.875rem",
                fontWeight: 700,
                transition: "color 0.15s ease",
                color: theme.palette.text.primary,
                lineHeight: 1.2,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {stock.symbol}
            </Typography>
            {exchange && (
              <Typography
                sx={{
                  fontSize: "0.5625rem",
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.05)",
                  px: 0.5,
                  py: 0.125,
                  borderRadius: 0.5,
                  flexShrink: 0,
                }}
              >
                {exchange}
              </Typography>
            )}
            {!isAccessible && proBadge}
          </Box>
          {/* Line 2: company name, truncating rather than pushing anything */}
          <Typography
            sx={{
              fontSize: "0.6875rem",
              color: theme.palette.text.secondary,
              lineHeight: 1.25,
              mt: 0.25,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {stock.name}
          </Typography>
          {/* Line 3: the day pair, and the after-hours pair beside it
              behind a divider when the market is closed */}
          <Box sx={{ display: "flex", alignItems: "stretch", gap: 1, mt: 0.5, minWidth: 0 }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                ref={priceRef}
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 700,
                  color: theme.palette.text.primary,
                  lineHeight: 1.25,
                  fontVariantNumeric: "tabular-nums",
                  whiteSpace: "nowrap",
                }}
              >
                {formatStockPrice({ price: stock.price, currency })}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  color: metricColor,
                  lineHeight: 1.25,
                  fontVariantNumeric: "tabular-nums",
                  whiteSpace: "nowrap",
                }}
              >
                {metricText}
              </Typography>
            </Box>
            {showAfterHours && afterHoursPrice !== undefined && (
              <>
                <Box
                  sx={{
                    width: "1px",
                    alignSelf: "stretch",
                    backgroundColor: panelBorder(isDark),
                    flexShrink: 0,
                  }}
                />
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.625, minWidth: 0 }}>
                  <NightsStayIcon
                    sx={{ fontSize: 12, color: theme.palette.text.disabled, flexShrink: 0 }}
                  />
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      ref={ahPriceRef}
                      sx={{
                        fontSize: "0.8125rem",
                        fontWeight: 600,
                        color: theme.palette.text.primary,
                        lineHeight: 1.25,
                        fontVariantNumeric: "tabular-nums",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {formatStockPrice({ price: afterHoursPrice, currency })}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.6875rem",
                        fontWeight: 600,
                        color: isAfterHoursPositive ? "#22c55e" : "#ef4444",
                        lineHeight: 1.25,
                        fontVariantNumeric: "tabular-nums",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {ahMetricText}
                    </Typography>
                  </Box>
                </Box>
              </>
            )}
          </Box>
        </Box>
        {/* The day's shape claims EVERYTHING the identity column leaves, at
            every width — the trend is the reason the row is scannable at a
            glance, so it gets the card's slack rather than the text doing. */}
        {showChart && (
          <Box sx={{ flex: 1, minWidth: 0, height: 52 }}>
            <MiniChart
              ticker={stock.symbol}
              width="responsive"
              height={52}
              strokeWidth={3}
              changePercentage={stock.changePct}
            />
          </Box>
        )}
      </Box>,
    );
  }

  return gated(
    <Box
      role="button"
      tabIndex={0}
      aria-label={gatedLabel}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      sx={{
        ...focusRingSx,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        p: isLarge ? 1.75 : 1.25,
        borderRadius: 1.5,
        backgroundColor: isDark ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.02)",
        border: isDark ? "1px solid rgba(255, 255, 255, 0.1)" : "1px solid rgba(0, 0, 0, 0.1)",
        cursor: "pointer",
        opacity: isAccessible ? 1 : 0.6,
        WebkitTapHighlightColor: "transparent",
        transition: "background-color 0.2s ease, border-color 0.2s ease",
        ...(isAccessible && {
          "&:hover": {
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.04)",
            borderColor: isDark ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.15)",
          },
          "&:active": {
            backgroundColor: isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)",
            transform: "scale(0.98)",
          },
        }),
        // A locked card presses too — it just leads somewhere else.
        ...(!isAccessible && {
          "&:hover": { borderColor: "rgba(34, 197, 94, 0.45)" },
          "&:active": { transform: "scale(0.98)" },
        }),
      }}
    >
      <Logo src={getSymbolImageUrl(stock.symbol)} alt={stock.symbol} ticker={stock.symbol} size={isLarge ? 36 : 30} />
      {/* Large cards fix the identity column so the chart can claim the
          middle; the name ellipsizes rather than pushing the chart around. */}
      <Box sx={isLarge ? { minWidth: 0, width: 210, flexShrink: 0 } : { minWidth: 0, flex: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: isLarge ? "0.875rem" : "0.8125rem",
              fontWeight: 600,
              color: theme.palette.text.primary,
              lineHeight: 1.25,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {stock.symbol}
          </Typography>
          {!isAccessible && proBadge}
        </Box>
        <Typography
          sx={{
            fontSize: isLarge ? "0.75rem" : "0.6875rem",
            color: theme.palette.text.secondary,
            lineHeight: 1.25,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {stock.name}
        </Typography>
      </Box>

      {/* Micro trend — the day's shape at a glance. Large cards let it run
          the whole middle of the card; regular cards keep a fixed stub. */}
      {showChart && isLarge && (
        <Box sx={{ flex: 1, minWidth: 0, height: 34, display: { xs: "none", sm: "block" } }}>
          <MiniChart
            ticker={stock.symbol}
            width="responsive"
            height={34}
            strokeWidth={1.75}
            changePercentage={stock.changePct}
          />
        </Box>
      )}
      {showChart && !isLarge && (
        <Box sx={{ width: 76, height: 26, flexShrink: 0, display: { xs: "none", sm: "block" } }}>
          <MiniChart
            ticker={stock.symbol}
            width={76}
            height={26}
            strokeWidth={1.25}
            changePercentage={stock.changePct}
          />
        </Box>
      )}
      {/* Large cards without room for a chart still push prices right */}
      {isLarge && <Box sx={{ flex: 1, display: { xs: "block", sm: "none" } }} />}

      {/* Day + after-hours stacks, divided like the compact stock cards */}
      <Box sx={{ display: "flex", alignItems: "stretch", gap: 1, flexShrink: 0 }}>
        <Box sx={{ minWidth: 0, textAlign: "right" }}>
          <Typography
            ref={priceRef}
            sx={{
              fontSize: isLarge ? "0.875rem" : "0.8125rem",
              fontWeight: 600,
              color: theme.palette.text.primary,
              lineHeight: 1.25,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {formatStockPrice({ price: stock.price, currency })}
          </Typography>
          <Typography
            sx={{
              fontSize: isLarge ? "0.75rem" : "0.6875rem",
              fontWeight: 600,
              color: changeColor,
              lineHeight: 1.25,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {dayPctText}
          </Typography>
        </Box>
        {showAfterHours && afterHoursPrice !== undefined && (
          <>
            <Box
              sx={{
                width: "1px",
                alignSelf: "stretch",
                backgroundColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
                flexShrink: 0,
              }}
            />
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
              <NightsStayIcon sx={{ fontSize: 14, color: theme.palette.text.disabled, flexShrink: 0 }} />
              <Box sx={{ minWidth: 0, textAlign: "right" }}>
                <Typography
                  ref={ahPriceRef}
                  sx={{
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    lineHeight: 1.25,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {formatStockPrice({ price: afterHoursPrice, currency })}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.6875rem",
                    fontWeight: 600,
                    color: isAfterHoursPositive ? "#22c55e" : "#ef4444",
                    lineHeight: 1.25,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {isAfterHoursPositive ? "+" : ""}
                  {afterHoursChangePercentage.toFixed(2)}%
                </Typography>
              </Box>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

// Between snapshot ticks only rows whose displayed values moved re-render;
// the AH hooks re-render their own card internally when their data changes.
export default memo(
  CompanyPulseCard,
  (prev, next) =>
    prev.stock.symbol === next.stock.symbol &&
    prev.stock.name === next.stock.name &&
    prev.stock.price === next.stock.price &&
    prev.stock.changePct === next.stock.changePct &&
    prev.stock.change === next.stock.change &&
    prev.stock.marketCap === next.stock.marketCap &&
    prev.stock.exchange === next.stock.exchange &&
    prev.showChart === next.showChart &&
    prev.size === next.size &&
    prev.show === next.show
);
